//@ts-check
import { logger } from "../../logger/Logger";
import { LmvMatrix4 as Matrix4 } from '../../wgs/scene/LmvMatrix4';
import { LmvBox3 as Box3, LmvBox3 } from '../../wgs/scene/LmvBox3';
import { Model } from "../../application/Model";
import { MeshFlags } from '../../wgs/scene/MeshFlags';
import THREE from "three";
import * as et from "../../application/EventTypes";
import { RcsExtractorCore } from './RcsExtractorCore.js';
import { RcsParser } from './RcsParser.js';
import { setRcsFileLoaderReference } from './RcsIO.js';
import { isLocalhostUrl } from './RcsUrlUtils.js';
import { MAX_LOD_LEVELS, USE_OLD_RANGER, INSTANCE_TREE_MODE, RCS_NUM_IN_FLIGHT_REQUESTS_HTTP } from './RcsConstants.js';
import { getRequiredLodLevels } from './RcsLodMapping.js';
import { ProgressState } from '../../application/ProgressState';
import { FileLoaderManager } from "../../application/FileLoaderManager";
import { BufferGeometryUtils } from '../../wgs/scene/BufferGeometry';
import { getRcsStageConfiguration, StageNames } from '../../wgs/scene/out-of-core-tile-manager/tasks/defaultStageConfiguration';
import { USE_OUT_OF_CORE_TILE_MANAGER, USE_WEBGPU, RCS_POINTCLOUD_MATERIAL_ID } from '../../wgs/globals';
import { fromUrlSafeBase64 } from '../../globals';
import { initLoadContext, getDeveloperApiUrl } from "../net/endpoints";
import { ROOT_LOADED_PROGRESS } from "../main/constants.js";
import { ViewingService } from "../net/Xhr";
import { RcsSimplePropDbLoader } from './RcsSimplePropDbLoader.js';
import { RcsDebugOctreePropDbLoader } from './RcsDebugOctreePropDbLoader.js';
import { RcsPackage } from './RcsPackage.js';
import { FRAGMENTS_UNLOADED_EVENT } from "../../application/EventTypes";
import { rcsWorkerPool } from "./RcsWorkerPool";

/** @type {boolean} One-time guard for the localhost dev-load warning in {@link getStageConfiguration}. */
let localhostLoadWarningLogged = false;

/** @import { Viewer3DImpl } from "../../application/Viewer3DImpl"; */
/** @import { MeshData } from '../../wgs/scene/BufferGeometry' */
/** @import { OutOfCoreTileManagerStageConfiguration } from '../../wgs/scene/out-of-core-tile-manager/OutOfCoreTileManager' */
/** @typedef {import('../../wgs/scene/BufferGeometry').LeanBufferGeometry} LeanBufferGeometry */


// ── RCS Type Definitions ─────────────────────────────────────────────

/**
 * Members shared by every octree node, regardless of whether it is a leaf.
 * @typedef {Object} OctreeNodeBase
 * @property {number}    id - Node ID
 * @property {number[]}  boundingBox - Bounding box [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number[]}  children - Child node IDs (empty for leaves)
 * @property {{ svoDataSize?: number, leafIndex?: number, svoDataOffset?: number }} [fileOffsets] - Developer-tool scratch data (populated by the RcsBvhExtractor JSON exporter).
 * @property {number}    [totalLeafVolume] - Total volume of all leaf nodes below this node (computed during build)
 */

/**
 * Members that are present only on umbrella leaf nodes (`isLeaf === true`).
 * @typedef {Object} OctreeLeafData
 * @property {true}      isLeaf - Discriminant: always `true` for leaf nodes
 * @property {number}    fragmentId - Fragment ID (direct mapping to the umbrella leaf index)
 * @property {number[]}  numSvoLeafNodes - SVO leaf point counts per level
 * @property {number[]}  numNonLeafNodes - SVO interior-node counts per level
 * @property {number[]}  nodeBounds - Tight AABB reported by the source leaf
 */

/**
 * Umbrella leaf node: owns SVO point data and has no children.
 * @typedef {OctreeNodeBase & OctreeLeafData} OctreeLeafNode
 */

/**
 * Members that are present only on interior octree nodes (`isLeaf === false`).
 * @typedef {Object} OctreeInteriorData
 * @property {false} isLeaf - Discriminant: always `false` for interior nodes
 */

/**
 * Interior octree node: has children and no SVO leaf data.
 * @typedef {OctreeNodeBase & OctreeInteriorData} OctreeInteriorNode
 */

/**
 * Octree node from the source umbrella octree structure.
 *
 * Discriminated union on the `isLeaf` flag: the leaf-only members
 * (`fragmentId`, `numSvoLeafNodes`, `numNonLeafNodes`, `nodeBounds`) are present
 * if and only if `isLeaf === true`. Narrow with `if (node.isLeaf)` to access them.
 * @typedef {OctreeLeafNode | OctreeInteriorNode} OctreeNode
 */

/**
 * Complete octree structure with annotated volumes.
 * @typedef {Object} RcsOctreeStructure
 * @property {OctreeNode[]} nodes - Array of octree nodes with totalLeafVolume annotations
 */

/**
 * Return value of {@link RcsFileLoader.getStats}.
 * @typedef {Object} RcsLoadStats
 * @property {Object<number, number>} lodNodeCounts - Loaded nodes per LOD level (only updated when {@link RcsFileLoader.setLoadStatsTrackingEnabled} is on)
 * @property {number} inFlightRequests - Number of outstanding RCS worker fetch/decode requests
 * @property {number} inFlightRequestsSize - Total byte size of in-flight worker requests
 * @property {number} totalReceivedBytes - Cumulative bytes read from RCS files (I/O + worker)
 * @property {number} nodesLoadedPerSecond - Fragments activated in the last second (only non-zero when stats tracking is enabled)
 * @property {number} totalNodesLoaded - Cumulative fragment activations since stats tracking was enabled
 */

/**
 * Decoded point data returned by the RCS worker.
 * @typedef {Object} RcsDecodedPoints
 * @property {number}       pointCount - Number of points
 * @property {Float32Array} interleavedData - Interleaved XYZRGB (6 floats per point)
 */

/**
 * Per-level decoded points returned by the RCS_DECODE_BY_LEVEL worker.
 * @typedef {Object} RcsLevelData
 * @property {Float32Array} interleavedData - Interleaved XYZRGB for this level
 * @property {number}       count - Number of points at this octree level
 * @property {number}       level - Octree level index
 */

/**
 * Result of RCS_DECODE_BY_LEVEL worker operation.
 * @typedef {Object} RcsLevelDecodeResult
 * @property {RcsLevelData[]} pointsByLevel - One entry per octree level that contained points
 * @property {{ offsetX: number, offsetY: number, offsetZ: number }} transformData - SVO-local → world translation applied to decoded coordinates
 */

/**
 * Options passed to {@link RcsFileLoader.loadFile}.
 * @typedef {Object} RcsLoadOptions
 * @property {boolean}  [skipPropertyDb] - When true, skip building the synthetic property database
 * @property {{ sortFuncType?: string }} [bvhOptions] - Options forwarded to BVH construction in {@link RcsPackage}
 * @property {THREE.Vector3|{ x: number, y: number, z: number }} [globalOffset] - Shared offset for multi-model aggregation
 */

/**
 * RCS File Loader for LMV — loads RCS point cloud files and creates an
 * out-of-core rendering structure for progressive streaming.
 *
 * Model data (metadata, fragments, BVH, per-node arrays) is held by
 * {@link RcsPackage}, which is the `myData` object for `Model`.
 * The loader is responsible for file I/O, material creation, fragment
 * activation/deactivation, and progress reporting.
 */
export class RcsFileLoader {

  /** @type {number} */
  static loadedFragments = 0;
  /** @type {Object.<number, number>} */
  static lodNodeCounts = {};
  /** @type {number} */
  static totalReceivedBytes = 0;
  /** @type {Array<number>} */
  static nodeLoadTimestamps = [];
  /** @type {number} */
  static totalNodesLoaded = 0;
  /** @type {number} */
  static lastStatsSecond = -1;
  /** @type {number} */
  static cachedNodesPerSecond = 0;
  /** @type {boolean} */
  static loadStatsTrackingEnabled = false;

  /**
   * Enable or disable per-node load statistics (for debugging overlays).
   * Off by default so {@link nodeLoadTimestamps} does not grow during normal use.
   * @param {boolean} enabled - When true, {@link getStats} tracks per-LOD counts and load rate
   */
  static setLoadStatsTrackingEnabled(enabled) {
    this.loadStatsTrackingEnabled = enabled;
    if (!enabled) {
      this.nodeLoadTimestamps = [];
      this.cachedNodesPerSecond = 0;
      this.lastStatsSecond = -1;
    }
  }

  /**
   * Global loading statistics.
   * @returns {RcsLoadStats}
   */
  static getStats() {
    const now = performance.now();
    const currentSecond = Math.floor(now / 1000);

    if (currentSecond !== this.lastStatsSecond) {
      const oneSecondAgo = now - 1000;
      while (this.nodeLoadTimestamps.length > 0 && this.nodeLoadTimestamps[0] < oneSecondAgo) {
        this.nodeLoadTimestamps.shift();
      }
      this.cachedNodesPerSecond = this.nodeLoadTimestamps.length;
      this.lastStatsSecond = currentSecond;
    }

    return {
      lodNodeCounts: this.lodNodeCounts,
      inFlightRequests: rcsWorkerPool.inFlightRequests,
      inFlightRequestsSize: rcsWorkerPool.inFlightRequestsSize,
      totalReceivedBytes: this.totalReceivedBytes + rcsWorkerPool.totalReceivedBytes,
      nodesLoadedPerSecond: this.cachedNodesPerSecond,
      totalNodesLoaded: this.totalNodesLoaded
    };
  }

  /**
   * @param {Viewer3DImpl} parent - The viewer implementation that owns this loader instance
   */
  constructor(parent) {
    /** @type {Viewer3DImpl} */
    this.viewer3DImpl = parent;
    /**
     * Single-use guard: once {@link loadFile} has started this loader must not
     * be reused (same convention as SvfLoader / OtgLoader). Not cleared on
     * success or error — only {@link dtor} resets it.
     * @type {boolean}
     */
    this.loading = false;
    /** @type {string|null} */
    this.currentLoadPath = null;
    /** @type {string|null} */
    this.urn = null;
    /** @type {string|null} */
    this.token = null;
    /** @type {Function|null} */
    this._onProgress = null;
    /** @type {Function|null} */
    this._onError = null;
    /** @type {Matrix4} */
    this._tmpMatrix = new Matrix4();
    /** @type {Box3} */
    this._tmpBox = new Box3();
    /** @type {number} */
    this.loadTime = 0;
    /** @type {boolean} */
    this.notifiesFirstPixel = true;
    /** @type {RcsExtractorCore|null} */
    this.rcsExtractor = null;
    /** @type {RcsPackage} - The model data package */
    // @ts-expect-error is null, assigned on load
    this.rcs = null;
    /** @type {THREE.PointCloudMaterial|null} */
    this.pointCloudMaterial = null;
    /** @type {{queryParams?: string, headers?: {Authorization?: string}}} */
    // @ts-expect-error is null, assigned on load
    this.loadContext = null;
    /** @type {RcsLoadOptions} */
    // @ts-expect-error is null, assigned on load
    this.options = null;
    /** @type {Model} */
    // @ts-expect-error is null, assigned on load
    this.model = null;

    // Progress monitoring
    /** @type {number} */
    this._coarseNodesLoaded = 0;
    /** @type {number} - Last progress percent we surfaced; used for ≥5% throttling. */
    this._lastReportedProgress = ROOT_LOADED_PROGRESS;

    rcsWorkerPool.register();
  }

  /**
   * Release extractor, property DB, worker-pool registration, and viewer references.
   * Resets the single-use {@link loading} guard so a new loader instance can be created.
   */
  dtor() {
    if (this.rcsExtractor) {
      this.rcsExtractor.close().catch((e) => logger.error("Error closing RCS extractor:", e));
      this.rcsExtractor = null;
    }

    if (this.rcs && this.rcs.propDbLoader) {
      this.rcs.propDbLoader.dtor();
      this.rcs.propDbLoader = null;
    }

    rcsWorkerPool.unregister();

    // @ts-expect-error is null
    this.rcs = null;
    this.pointCloudMaterial = null;
    // @ts-expect-error is null
    this._tmpMatrix = null;
    // @ts-expect-error is null
    this._tmpBox = null;
    // @ts-expect-error is null
    this.viewer3DImpl = null;
    this.loading = false;
  }

  /**
   * Load an RCS file and create the Model.
   * @param {string} url - Direct file/HTTP URL, or `rcs:<urn>` for ranger-hosted scans
   * @param {RcsLoadOptions} options - Load-time options forwarded to {@link RcsPackage}
   * @param {function(Error|null, Model): void} [onLoad] - Called with `(null, model)` when the model root is ready
   * @param {function({type: string, percent: number, message: string}): void} [onProgress] - Coarse-loading progress callback
   * @param {function(Error): void} [onError] - Called if root load fails
   * @returns {boolean} `false` if already loading or the loader was destructed; `true` if load was started
   */
  loadFile(url, options, onLoad, onProgress, onError) {
    if (this.loading) {
      logger.warn("RCS loader is already loading a file");
      return false;
    }

    if (!this.viewer3DImpl) {
      logger.log("RCS loader was already destructed. So no longer usable.");
      return false;
    }

    this.loadContext = initLoadContext({});
    // initLoadContext calls getQueryParams, which may attach derivative-CDN params
    // (domain, bypassds, etc.). RCS does not use that pipeline — only auth headers
    // and occasional direct API URLs — so clear them. Passing queryParams: '' into
    // initLoadContext would not work (empty string is falsy and gets overwritten).
    this.loadContext.queryParams = '';

    this.loading = true;
    this.loadTime = Date.now();
    this.options = options;
    this.currentLoadPath = url;

    this._loadModelRoot(url, options, onLoad, onProgress, onError);

    return true;
  }

  /**
   * Parse the RCS header, build {@link RcsPackage}, create the {@link Model}, and
   * dispatch root-load events. Fragment streaming is driven by the OOC tile manager
   * after this returns.
   * @private
   * @param {string} url - Same URL passed to {@link loadFile}
   * @param {RcsLoadOptions} options - Load options stored on the loader for later use
   * @param {function(Error|null, Model): void} [onLoad] - Root-load success callback
   * @param {function({type: string, percent: number, message: string}): void} [onProgress] - Stored for coarse-progress reporting
   * @param {function(Error): void} [onError] - Root-load failure callback
   * @returns {Promise<void>}
   */
  async _loadModelRoot(url, options, onLoad, onProgress, onError) {
    // Assign progress/error callbacks first so they are available to any
    // progress event triggered (now or later) during root load — e.g.
    // onModelRootLoadDone / _setupProgressMonitoring and the coarse-node
    // streaming path (onCoarseNodeLoaded -> _reportCoarseLoadingProgress).
    this._onProgress = onProgress ?? null;
    this._onError = onError ?? null;

    // URN is needed downstream for WebSocket auth (RcsWebSocketBatcher) and gets
    // forwarded to the worker via LoadRcsNodeTask. For `rcs:` URLs we parse it
    // from the URL itself; otherwise we leave it null and the WebSocket path
    // will not be used (createRcsIO falls back to BrowserRcsIO).
    let urn = null;

    // For the old ranger mode, we generate a signed URL for the data file from the Data Management API.
    // For the new ranger mode, we use the URN to identify the resource and no data URL is needed.
    let rcsFileUrl = /** @type {string|null} */null;
    if (url.startsWith('rcs:')) {
      urn = url.substring(4).replace('.rcs', '');
      if (USE_OLD_RANGER) {
        // Very hacky, we use the global export because it gets overwritte by viewer3d.html.
        // We need a cleaner way to pass those, maybe we options?
        // @ts-ignore
        const projectId = globalThis.Autodesk.Viewing.Private.getParameterByName('projectId');
        // @ts-ignore
        const docsEnv = globalThis.Autodesk.Viewing.Private.getParameterByName('docsEnv') ?? 'staging';
        let developerApiUrl = getDeveloperApiUrl();

        let dataManagementApiSuffix = docsEnv === 'QA' ? 'forge-dm-qa/data/v1/projects/' : '/data/v1/projects/';
        const dataManagementAPIUrl = developerApiUrl + `/${dataManagementApiSuffix}/b.${encodeURIComponent(projectId)}/versions/${encodeURIComponent(fromUrlSafeBase64(urn))}`;

        let dataManagementAPIResponse = await new Promise((resolve, fail) => {
          ViewingService.rawGet(dataManagementAPIUrl, '', '', resolve, fail, this.loadContext);
        });

        dataManagementAPIResponse = JSON.parse(dataManagementAPIResponse);
        let storageUrl = dataManagementAPIResponse.data?.relationships?.storage?.meta?.link?.href;
        if (!storageUrl) {
          throw new Error('Storage URL not found in Data Management API response');
        }
        let signedDataUrl = await ViewingService.getSignedS3DownloadUrl(storageUrl, 60, false);
        rcsFileUrl = signedDataUrl;
      }
    } else {
      // If the URL points directly to a RCS file, we set it here
      rcsFileUrl = url;
    }

    this.urn = urn;

    // Extract the bearer token from the Authorization header set by initLoadContext.
    // Forwarded to the worker via LoadRcsNodeTask's workerData.
    const authHeader = this.loadContext.headers?.Authorization;
    const bearerMatch = authHeader?.match(/^bearer\s+(\S+)/i);
    this.token = bearerMatch ? bearerMatch[1] : null; // gitleaks:allow  - false positive, no secret in code

    // Initialize RCS extractor
    this.rcsExtractor = new RcsExtractorCore(rcsFileUrl, urn, this.token);

    try {
      if (!USE_OUT_OF_CORE_TILE_MANAGER) {
        throw new Error("OutOfCoreTileManager is not enabled");
      }

      const t0 = Date.now();

      await this.rcsExtractor.open();
      const { header, chunks } = await this.rcsExtractor.readHeaderAndChunkDirectory();

      const [umbrellaLeafNodes, umbrellaExtendedData] = await Promise.all([
      this.rcsExtractor.readUmbrellaOctreeData(),
      this.rcsExtractor.readUmbrellaExtendedData()]
      );
      const octreeStructure = RcsParser.buildOctreeStructure(umbrellaLeafNodes, umbrellaExtendedData);

      // Build fragment ID map: (umbrellaIndex, lodLevel) -> fragId
      const numUmbrellaNodes = umbrellaLeafNodes.length;
      const fragmentIdMap = new Map();
      let nextFragId = 0;
      for (let ui = 0; ui < numUmbrellaNodes; ui++) {
        for (const lod of getRequiredLodLevels(umbrellaLeafNodes[ui].numSvoLeafNodes)) {
          fragmentIdMap.set(ui * MAX_LOD_LEVELS + lod, nextFragId++);
        }
      }

      // Build RcsPackage (BVH, per-node arrays, fragment data)
      this.rcs = new RcsPackage(
        header, chunks, umbrellaLeafNodes, octreeStructure, fragmentIdMap, options
      );

      // Create the Model using the package as its data
      this.model = new Model(this.rcs, this);
      // Empty / placeholder models (no umbrella leaves) have no BVH — see
      // RcsPackage._initEmptyModel. setBVH(null) would throw in ModelIteratorBVH.
      if (this.rcs.bvh) {
        this.model.setBVH(this.rcs.bvh, true);
      }
      this.onModelRootLoadDone();

      if (onLoad) {
        onLoad(null, this.model);
      }

      // @ts-ignore - dispatchEvent is inherited via a MixIn that is not supported in TS
      this.viewer3DImpl.api.dispatchEvent({
        type: et.MODEL_ROOT_LOADED_EVENT,
        rcs: this.rcs,
        model: this.model
      });

      // Start progress monitoring after MODEL_ROOT_LOADED so the final
      // GEOMETRY_LOADED (onLoadComplete) for empty models cannot precede it.
      this._setupProgressMonitoring();

      const t1 = Date.now();
      logger.info("RCS root load: " + (t1 - t0));

    } catch (error) {
      logger.error("Error loading RCS file:", error);
      if (onError) {
        onError(error);
      }
    }
  }

  /**
   * Finalize the model root: register the point-cloud material, optionally build
   * the property DB, and signal `ROOT_LOADED` progress to the viewer.
   * @private
   */
  onModelRootLoadDone() {
    this.rcs.fragments.numLoaded = 0;

    this._createAndRegisterMaterial();

    if (!this.options.skipPropertyDb) {
      this.loadPropertyDb();
    }

    this.viewer3DImpl._signalNoMeshes();
    this.viewer3DImpl.signalProgress(ROOT_LOADED_PROGRESS, ProgressState.ROOT_LOADED, this.model, this.rcs.metadata.stats.num_fragments);
  }

  /**
   * Create and load the synthetic {@link RcsPropDbLoader} instance tree.
   * @private
   */
  loadPropertyDb() {
    // Viewer3D implements the EventDispatcher interface via a mixin not supported in TS
    const eventTarget = /** @type {import('../../application/EventDispatcher').EventDispatcher} */ /** @type {unknown} */this.viewer3DImpl.api;
    this.rcs.propDbLoader = INSTANCE_TREE_MODE === 'simple' ?
    new RcsSimplePropDbLoader(this.model, eventTarget) :
    new RcsDebugOctreePropDbLoader(this.model, eventTarget);
    this.rcs.propDbLoader.load();
  }

  /**
   * Create the shared {@link THREE.PointCloudMaterial} and register it with the
   * material manager under {@link RCS_POINTCLOUD_MATERIAL_ID}.
   * @private
   */
  _createAndRegisterMaterial() {
    this.pointCloudMaterial = new THREE.PointCloudMaterial({
      name: 'RCS_PointCloud',
      vertexColors: true,
      size: 2.0
    });

    const matHash = this.viewer3DImpl.matman()._getMaterialHash(this.model, RCS_POINTCLOUD_MATERIAL_ID);
    this.viewer3DImpl.matman().addMaterial(matHash, this.pointCloudMaterial);
  }

  /**
   * Create point geometry, add it to the model, set up the mesh, and
   * activate the fragment. This is the single entry point for turning
   * decoded (or pre-allocated) point data into a visible fragment.
   *
   * Underscore prefix: "protected" for this class — only the out-of-core loading task
   * (LoadRcsNodeTask) should call this, not host or external integration code.
   *
   * @param {number} fragId - Fragment to activate
   * @param {Object} options - Decoded (or placeholder) point data for the fragment
   * @param {number} options.pointCount - Number of points
   * @param {Float32Array} [options.interleavedData] - Interleaved XYZRGB data. If omitted, a NaN-filled buffer is allocated (for HLOD inner nodes that are filled later).
   * @returns {LeanBufferGeometry} The created geometry
   */
  _addAndActivateFragment(fragId, options) {
    const geometry = this._createPointGeometry(fragId, options);

    const geomId = this.model.getGeometryList().addGeometry(geometry, 1);

    const fragmentList = this.model.getFragmentList();
    fragmentList.getOriginalWorldMatrix(fragId, this._tmpMatrix);

    const mesh = this.viewer3DImpl.setupMesh(
      this.model, geometry, RCS_POINTCLOUD_MATERIAL_ID, this._tmpMatrix
    );
    this.model.activateFragment(fragId, mesh);
    this.rcs.fragments.numLoaded++;

    this.viewer3DImpl.signalNewGeometryAdded(geomId, this.model);

    if (RcsFileLoader.loadStatsTrackingEnabled) {
      const nodeId = this.model.getIterator().getBVH().fragId2nodeId[fragId];
      const lodLevel = this.rcs.getNodeLodLevel(nodeId);

      RcsFileLoader.lodNodeCounts[lodLevel] = (RcsFileLoader.lodNodeCounts[lodLevel] || 0) + 1;
      RcsFileLoader.nodeLoadTimestamps.push(performance.now());
      const oneSecondAgo = performance.now() - 1000;
      while (RcsFileLoader.nodeLoadTimestamps.length > 0 &&
      RcsFileLoader.nodeLoadTimestamps[0] < oneSecondAgo) {
        RcsFileLoader.nodeLoadTimestamps.shift();
      }
      RcsFileLoader.totalNodesLoaded++;
    }

    return geometry;
  }

  /**
   * Create LMV BufferGeometry with interleaved point data.
   *
   * The geometry's local-space bounding box is derived from the BVH node that
   * owns the fragment. Fragments are translated so that their world-space
   * min corner sits at the local origin (see `_initFragmentData` in
   * `RcsPackage`), so the local box goes from (0,0,0) to (size-x, size-y, size-z).
   * Setting a real bounding box ensures fragment world bounds stay finite when
   * `FragmentList.setMesh` writes them back from `geometry.boundingBox`, and
   * avoids downstream consumers (culling, ray-casting, model bbox) seeing
   * infinite values.
   *
   * @private
   * @param {number} fragId - Fragment ID (used to look up the BVH node bounds)
   * @param {Object} options - Point data used to populate the geometry buffers
   * @param {number} options.pointCount - Number of points
   * @param {Float32Array} [options.interleavedData] - Interleaved XYZRGB data
   * @returns {LeanBufferGeometry}
   */
  _createPointGeometry(fragId, options) {
    const { pointCount, interleavedData } = options;
    const stride = 6;

    let vb = interleavedData;
    if (!vb) {
      vb = new Float32Array(pointCount * 6);
      vb.fill(NaN);
    }

    // WebGL requires an index buffer.
    let indices = null;
    if (!USE_WEBGPU) {
      const useUint32 = pointCount > 65535;
      indices = useUint32 ? new Uint32Array(pointCount) : new Uint16Array(Math.ceil(pointCount / 2) * 2);
      for (let i = 0; i < pointCount; i++) {
        indices[i] = i;
      }
      if (indices.length !== pointCount) {
        indices[pointCount] = 0;
      }
    }

    /** @type {{ mesh: MeshData }} */
    const meshData = {
      mesh: {
        vb: vb,
        vbstride: stride,
        vblayout: {
          position: { offset: 0, bytesPerItem: 4, itemSize: 3 },
          color: { offset: 3, bytesPerItem: 4, itemSize: 3, normalized: false }
        },
        indices: indices,
        isPoints: true,
        pointSize: 2.5,
        boundingBox: this._getLocalFragmentBoundingBox(fragId)
      }
    };

    return BufferGeometryUtils.meshToGeometry(meshData);
  }

  /**
   * Compute the local-space bounding box for a fragment from its world-space
   * BVH box. Fragments are translated so their world-space min corner sits at
   * the local origin, so the local box is (0,0,0) → (size-x, size-y, size-z).
   * @private
   * @param {number} fragId - Fragment whose world-space box in {@link RcsPackage.fragments} is converted to local space
   * @returns {LmvBox3} Local AABB with min at the origin and max at the fragment extent
   */
  _getLocalFragmentBoundingBox(fragId) {
    const boxes = this.rcs.fragments.boxes;
    const o = fragId * 6;
    const box = new LmvBox3();
    box.min.set(0, 0, 0);
    box.max.set(
      boxes[o + 3] - boxes[o],
      boxes[o + 4] - boxes[o + 1],
      boxes[o + 5] - boxes[o + 2]
    );
    return box;
  }

  /**
   * Deactivate a fragment (unload its geometry and make it invisible).
   * Called by the OOC tile manager when a node is demoted from GPU memory.
   * @param {number} fragId - Fragment to swap out of core and hide
   */
  _deactivateFragment(fragId) {
    const nodeId = this.model.getIterator().getBVH().fragId2nodeId[fragId];
    const lodLevel = this.rcs.getNodeLodLevel(nodeId);

    if (RcsFileLoader.loadStatsTrackingEnabled && RcsFileLoader.lodNodeCounts[lodLevel]) {
      RcsFileLoader.lodNodeCounts[lodLevel]--;
    }

    if (this.model.getFragmentList().isFragOff(fragId)) {
      return;
    }

    const fl = this.model.getFragmentList();
    const fragsUnloaded = fl.unload([fragId], MeshFlags.MESH_SWAPPED_OUT_OF_CORE);

    if (fragsUnloaded.length > 0) {
      const geomCache = this.viewer3DImpl.geomCache(/* create */false);
      for (const unloadedFragId of fragsUnloaded) {
        fl.geoms.removeInstance(fl.geomids[unloadedFragId], geomCache);
      }
      this.rcs.fragments.numLoaded -= fragsUnloaded.length;
      // @ts-ignore
      this.viewer3DImpl.api.dispatchEvent({ type: FRAGMENTS_UNLOADED_EVENT, model: this.model });
    }
  }

  /**
   * OOC stage pipeline configuration for RCS point-cloud streaming.
   * @returns {OutOfCoreTileManagerStageConfiguration[]}
   */
  getStageConfiguration() {
    const isLocalDevLoad = this.currentLoadPath &&
    !this.currentLoadPath.startsWith('rcs:') &&
    isLocalhostUrl(this.currentLoadPath);
    if (isLocalDevLoad) {
      if (!localhostLoadWarningLogged) {
        localhostLoadWarningLogged = true;
        logger.warn(
          `RCS: loading from localhost is for debugging only; ` +
          `reducing parallel geometry requests to ${RCS_NUM_IN_FLIGHT_REQUESTS_HTTP}.`
        );
      }
      return getRcsStageConfiguration().map((stage) =>
      stage.stage === StageNames.GEOMETRY_LOADED ?
      { ...stage, inAsyncPromotionLimit: RCS_NUM_IN_FLIGHT_REQUESTS_HTTP } :
      stage
      );
    }
    return getRcsStageConfiguration();
  }

  /**
   * Initialize loading-progress tracking.
   *
   * Progress is driven by the "coarse" node set (interior HLOD nodes + LOD-0
   * umbrella leaves) rather than total fragment count. Coarse fragments are
   * marked resident by RcsPackage.getLoadingScreenSpaceError, so once they
   * are all loaded the model has its complete coarsest-LOD representation
   * and we report 100%. Finer LOD-chain fragments (LOD 1+) keep streaming
   * on demand.
   *
   * @private
   */
  _setupProgressMonitoring() {
    this._coarseNodesLoaded = 0;
    this._lastReportedProgress = ROOT_LOADED_PROGRESS;

    this.viewer3DImpl.signalProgress(ROOT_LOADED_PROGRESS, ProgressState.LOADING, this.model);

    // Edge case: a model with no coarse nodes (empty / placeholder) is
    // already done as soon as the root has been loaded.
    if (this.rcs.numCoarseNodes === 0) {
      this._reportCoarseLoadingProgress(100);
    }
  }

  /**
   * Notify the loader that a coarse BVH node has finished loading.
   * Called by {@link LoadRcsNodeTask} after each coarse-node task executes.
   * No parameters — progress is derived from {@link RcsPackage.numCoarseNodes}.
   */
  onCoarseNodeLoaded() {
    // Guard against the callback being called asynchronously after teardown.
    if (!this.rcs || !this.viewer3DImpl) return;

    if (this.rcs.loadDone) {
      // TODO: This works for the moment, but at some point we will need the ability to unload
      //       coarse nodes to enable loading multiple point clouds. At that point, this might happen
      logger.warn("onCoarseNodeLoaded called after load was already done");
      return;
    }

    this._coarseNodesLoaded++;

    const total = this.rcs.numCoarseNodes;
    const loaded = this._coarseNodesLoaded;
    const progressPercent = Math.min(ROOT_LOADED_PROGRESS + Math.floor(loaded / total * (100 - ROOT_LOADED_PROGRESS)), 100);

    this._reportCoarseLoadingProgress(progressPercent);
  }

  /**
   * Surface the current coarse-loading progress to the onProgress callback
   * and the viewer's progress event.
   * @param {number} progressPercent - Current overall load progress in [0, 100].
   * @private
   */
  _reportCoarseLoadingProgress(progressPercent) {
    if (this.rcs.loadDone) return;

    //Throttle events to >=5% increments, but always fire the final 100% notification.
    if (progressPercent < this._lastReportedProgress + 5 && progressPercent < 100) {
      return;
    }
    this._lastReportedProgress = progressPercent;

    if (this._onProgress) {
      this._onProgress({
        type: 'progress',
        percent: progressPercent,
        message: `Loading point clouds... ${this._coarseNodesLoaded}/${this.rcs.numCoarseNodes} coarse nodes ready`
      });
    }

    if (progressPercent < 100) {
      this.viewer3DImpl.signalProgress(progressPercent, ProgressState.LOADING, this.model);
    } else {
      this.rcs.loadDone = true;
      // onLoadComplete signals 100% via signalProgress and dispatches
      // GEOMETRY_LOADED_EVENT, mirroring the OTG / SVF loaders.
      this.viewer3DImpl.onLoadComplete(this.model);
    }
  }
}

// Initialize RcsIO reference for byte tracking
setRcsFileLoaderReference(RcsFileLoader);

/**
 * Register the RCS file loader with the FileLoaderManager. Gated behind the
 * `EnablePointCloud` internal feature flag and invoked from the flag's
 * initialization callback in {@link FeatureFlags}, so RCS support is opt-in.
 * @private
 */
export function registerRcsFileLoader() {
  FileLoaderManager.registerFileLoader("rcs", ["rcs"], RcsFileLoader);
}